/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
export enum ValidationMessageCode {
  FIELD_FILE_SIZE_EXCEEDED = 'validation_field_file_size_exceeded',
  FIELD_EMPTY = 'validation_field_empty',
  FIELD_SIZE = 'validation_field_size',
  FIELD_FILE_CONTENT_TYPE_INVALID = 'validation_field_file_content_type_invalid',
  FIELD_FILE_EXTENSION_MISMATCH = 'validation_field_file_extension_mismatch',
  FIELD_MIN_SIZE = 'validation_field_min_size',
  FIELD_MAX_SIZE = 'validation_field_max_size',
  FIELD_DATE_PAST = 'validation_field_date_past',
  FIELD_INVALID = 'validation_field_invalid',
  FIELD_INVALID_EMAIL = 'validation_field_invalid_email',
  FIELD_DATE_FORMAT_INVALID = 'validation_field_date_format_invalid',
  FIELD_ASSIGN_BEARBEITER_NOT_EXIST = 'fe_only_validation_bearbeiter_not_exist',
  FIELD_VALUE_ALREADY_EXISTS = 'validation_field_value_already_exists',
  FILE_REQUIRED = 'validation_file_required',
  FIELD_NOT_VALID_JSON = 'validation_field_not_valid_json',
  FIELD_FITCONNECT_INVALID_KEY_JSON = 'validation_field_fitconnect_invalid_key_json',
  FIELD_FITCONNECT_INVALID_KEY_FORMAT = 'validation_field_fitconnect_invalid_key_format',
  FIELD_FITCONNECT_INVALID_KEY_TYPE = 'validation_field_fitconnect_invalid_key_type',
  FIELD_FITCONNECT_WRONG_KEY_PURPOSE = 'validation_field_fitconnect_wrong_key_purpose',
  FIELD_FITCONNECT_PRIVATE_KEYS_SWAPPED = 'validation_field_fitconnect_private_keys_swapped',
  FIELD_FITCONNECT_KEY_PAIR_MISMATCH = 'validation_field_fitconnect_key_pair_mismatch',
  FIELD_INVALID_HTTP_URL = 'validation_field_invalid_http_url',
  FIELD_BRIDGE_UNREACHABLE = 'validation_field_bridge_unreachable',
  FIELD_NAME_TOO_LONG = 'validation_field_name_too_long',
  MISSING_ZUARBEIT = 'missing_zuarbeit',
  PROCESSOR_IN_USE = 'processor_in_use',
  FIELD_CSR_NOT_READABLE = 'validation_field_csr_not_readable',
  FIELD_CSR_SIGNATURE_INVALID = 'validation_field_csr_signature_invalid',
  FIELD_CSR_KEY_TOO_WEAK = 'validation_field_csr_key_too_weak',
  CHANNEL_NOT_SUPPORTED = 'channel_not_supported',
  ZUGANG_NOT_RENEWABLE = 'zugang_not_renewable',
  CERT_MANAGER_UNAVAILABLE = 'cert_manager_unavailable',
  ZUGANGPUNKT_HAS_ACTIVE_ZUGAENGE = 'zugangpunkt_has_active_zugaenge',
}

export interface ValidationMessages {
  [code: string]: string;
}

export const VALIDATION_MESSAGES: ValidationMessages = {
  [ValidationMessageCode.FIELD_EMPTY]: 'Bitte {field} ausfüllen',
  [ValidationMessageCode.FIELD_MAX_SIZE]: '{field} darf höchstens {max} Zeichen enthalten',
  [ValidationMessageCode.FIELD_MIN_SIZE]: '{field} muss aus mindestens {min} Zeichen bestehen',
  [ValidationMessageCode.FIELD_SIZE]: '{field} muss mindestens {min} und darf höchstens {max} Zeichen enthalten',
  [ValidationMessageCode.FIELD_DATE_PAST]: 'Das Datum für {field} muss in der Zukunft liegen',
  [ValidationMessageCode.FIELD_INVALID]: 'Bitte {field} korrekt ausfüllen',
  [ValidationMessageCode.FIELD_INVALID_EMAIL]: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
  [ValidationMessageCode.FIELD_FILE_SIZE_EXCEEDED]: 'Anhänge größer {max}{unit} können nicht hinzugefügt werden.',
  [ValidationMessageCode.FIELD_ASSIGN_BEARBEITER_NOT_EXIST]: 'Der Bearbeiter existiert nicht',
  [ValidationMessageCode.FIELD_NOT_VALID_JSON]: 'Die hochgeladene Datei enthält kein gültiges Json',
  [ValidationMessageCode.FIELD_FITCONNECT_INVALID_KEY_JSON]:
    'Die hochgeladene Datei ist kein gültiges JSON. Bitte verwenden Sie die von FitConnect bereitgestellte JWK-Datei.',
  [ValidationMessageCode.FIELD_FITCONNECT_INVALID_KEY_FORMAT]:
    'Die hochgeladene Datei enthält keinen gültigen FitConnect-Schlüssel. Bitte verwenden Sie die von FitConnect bereitgestellte JWK-Datei.',
  [ValidationMessageCode.FIELD_FITCONNECT_INVALID_KEY_TYPE]:
    'Der hochgeladene FitConnect-Schlüssel ist ein öffentlicher Schlüssel. Für die Verschlüsselung wird der zugehörige private Schlüssel benötigt.',
  [ValidationMessageCode.FIELD_FITCONNECT_WRONG_KEY_PURPOSE]:
    'Der hochgeladene FitConnect-Schlüssel ist nicht für dieses Feld geeignet. Bitte überprüfen Sie, ob im Decryption-Feld der Decryption-Schlüssel und im Signing-Feld der Signing-Schlüssel hochgeladen ist.',
  [ValidationMessageCode.FIELD_FITCONNECT_PRIVATE_KEYS_SWAPPED]:
    'Die hochgeladenen privaten FitConnect-Schlüssel sind vertauscht: Der Decryption-Schlüssel liegt im Signing-Feld (oder umgekehrt). Bitte laden Sie jeden Schlüssel in das passende Feld.',
  [ValidationMessageCode.FIELD_FITCONNECT_KEY_PAIR_MISMATCH]:
    'Der hochgeladene private FitConnect-Schlüssel passt nicht zu dem bei FitConnect hinterlegten öffentlichen Schlüssel dieses Online-Dienstes.',
  [ValidationMessageCode.FIELD_INVALID_HTTP_URL]: 'Bitte geben Sie eine gültige URL ein, die mit http:// oder https:// beginnt.',
  [ValidationMessageCode.FIELD_BRIDGE_UNREACHABLE]:
    'Die eingegebene Adresse ist nicht erreichbar. Bitte prüfen Sie die URL und versuchen Sie es erneut.',
  [ValidationMessageCode.FIELD_NAME_TOO_LONG]: 'Der Name darf maximal 100 Zeichen lang sein.',
  [ValidationMessageCode.FIELD_DATE_FORMAT_INVALID]: 'Geben Sie ein gültiges Datum ein',
  [ValidationMessageCode.FIELD_FILE_CONTENT_TYPE_INVALID]: 'Erlaubte Dateiendungen: {fileExtensions}',
  [ValidationMessageCode.FIELD_FILE_EXTENSION_MISMATCH]: 'Dateiendung stimmt nicht mit Dateityp überein',
  [ValidationMessageCode.FIELD_VALUE_ALREADY_EXISTS]: '{value} bereits verwendet',
  [ValidationMessageCode.FILE_REQUIRED]: 'Bitte Datei hochladen',
  [ValidationMessageCode.MISSING_ZUARBEIT]: 'Bitte ausfüllen oder Dokumenten-Datei hochladen.',
  [ValidationMessageCode.PROCESSOR_IN_USE]:
    'Der Prozessor kann nicht gelöscht werden, solange ihm noch Onlinedienste zugeordnet sind. Bitte entfernen Sie zuerst die zugeordneten Onlinedienste.',
  [ValidationMessageCode.FIELD_CSR_NOT_READABLE]:
    'Die hochgeladene Datei enthält keine lesbare Zertifikatsanfrage. Bitte laden Sie die unveränderte CSR-Datei im PEM-Format hoch.',
  [ValidationMessageCode.FIELD_CSR_SIGNATURE_INVALID]:
    'Die Signatur der Zertifikatsanfrage ist ungültig. Bitte erzeugen Sie die Anfrage mit dem passenden privaten Schlüssel neu.',
  [ValidationMessageCode.FIELD_CSR_KEY_TOO_WEAK]:
    'Der Schlüssel in der Zertifikatsanfrage ist zu kurz. Zulässig sind RSA ab 3072 Bit und elliptische Kurven ab 256 Bit.',
  [ValidationMessageCode.CHANNEL_NOT_SUPPORTED]:
    'Für diesen Zugangspunkt können keine Client-Zertifikate ausgestellt werden, weil sein Eingang nicht über mTLS abgesichert ist.',
  [ValidationMessageCode.ZUGANG_NOT_RENEWABLE]:
    'Dieses Zertifikat kann nicht erneuert werden. Erneuern lassen sich nur aktive Zertifikate, die noch keinen Nachfolger haben.',
  [ValidationMessageCode.CERT_MANAGER_UNAVAILABLE]:
    'Das Zertifikat konnte nicht ausgestellt werden, weil der Zertifikatsdienst nicht rechtzeitig geantwortet hat. Bitte versuchen Sie es erneut.',
  [ValidationMessageCode.ZUGANGPUNKT_HAS_ACTIVE_ZUGAENGE]:
    'Für diesen Zugangspunkt existiert noch mindestens ein gültiges Zertifikat. Bitte widerrufen Sie es zuerst.',
};
